import React, { type ReactNode } from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router";
import { PersistGate } from "redux-persist/integration/react";
import type { Persistor } from "redux-persist";
import { QueryClientProvider } from "@tanstack/react-query";
import type { PicoAppEnum } from "jadro-pico/apps/PicoAppEnum";
import type { PicoRoleEnum } from "jadro-pico/enums/auth/PicoRoleEnum";
import { LanguageEnum } from "jadro-pico/enums/common/LanguageEnum";
import { configureOriginApp } from "./originApp";
import { createQueryClient } from "../Api/createQueryClient";
import { configureAuth } from "../Auth/configureAuth";
import RequireAuth from "../Auth/RequireAuth";
import { startPendingMutationsWatcher } from "../Offline/flushPendingMutations";
import type { OfflineQueueStore } from "../Offline/OfflineQueueStore";
import { OperationsProvider } from "../Operations/OperationsProvider";
import { ErrorResourcesContext } from "../Resources/errors/ErrorResourcesContext";
import type { ErrorResources } from "../Resources/errors/ErrorResources";
import { PicoComRoutes } from "../Routes/PicoComRoutes";

type ReduxStore = React.ComponentProps<typeof Provider>["store"];

export interface PicoAppConfig {
  store: ReduxStore & OfflineQueueStore;
  persistor: Persistor;
  clientId: PicoAppEnum;
  errorResources: ErrorResources;
  children: ReactNode;
  /** Off for an app that hosts its own login or serves public pages. */
  requiresAuth?: boolean;
  requiredRole?: PicoRoleEnum;
  /** Off where a late write is more dangerous than a lost one, i.e. administration. */
  offlineQueue?: boolean;
}

export function renderPicoApp({
  store,
  persistor,
  clientId,
  errorResources,
  children,
  requiresAuth = true,
  requiredRole,
  offlineQueue = true,
}: PicoAppConfig): void {
  const picoComApiUrl = import.meta.env.VITE_PICO_API_URL;
  const wsUrl = import.meta.env.VITE_PICO_WS_URL;
  const loginUrl = `${import.meta.env.VITE_PICO_COM_FE_URL}${PicoComRoutes.LOGIN.paths[LanguageEnum.CS]}`;

  configureAuth({ picoComApiUrl, loginUrl });
  configureOriginApp(clientId);
  if (offlineQueue) startPendingMutationsWatcher(store);

  const queryClient = createQueryClient();
  const operations = (
    <OperationsProvider wsUrl={wsUrl} clientId={clientId}>
      {children}
    </OperationsProvider>
  );

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <BrowserRouter>
            <QueryClientProvider client={queryClient}>
              <ErrorResourcesContext.Provider value={{ primary: errorResources }}>
                {requiresAuth ? (
                  <RequireAuth
                    picoComApiUrl={picoComApiUrl}
                    loginUrl={loginUrl}
                    requiredRole={
                      requiredRole ? { client: clientId, role: requiredRole } : undefined
                    }
                  >
                    {operations}
                  </RequireAuth>
                ) : (
                  operations
                )}
              </ErrorResourcesContext.Provider>
            </QueryClientProvider>
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </React.StrictMode>,
  );
}
